import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Avatar,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { extractErrorMessage } from "@api/axios";

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, deleteAccount } = useAuth();
  const { notify } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const displayLabel =
    user?.displayName?.trim() ||
    user?.email?.split("@")[0] ||
    t("account.nameFallback");

  const initial = (
    user?.displayName?.trim()?.[0] ||
    user?.email?.[0] ||
    "?"
  ).toUpperCase();

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
      notify(t("account.loggedOut"), "success");
      navigate("/", { replace: true });
    } catch {
      notify(t("account.logoutFailed"), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      setDeleteOpen(false);
      notify(t("account.deleted"), "success");
      navigate("/", { replace: true });
    } catch (e) {
      notify(extractErrorMessage(e, t("account.deleteFailed")), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560, mx: "auto" }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("account.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t("account.subtitle")}
      </Typography>
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              mb: 2,
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: "primary.main",
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.name")}
              </Typography>
              <Typography
                variant="h6"
                noWrap
                title={displayLabel}
                sx={{
                  fontWeight: 600,
                }}
              >
                {displayLabel}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            {t("account.email")}
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: "break-all", mb: 2 }}>
            {user?.email ?? t("common.notAvailable")}
          </Typography>

          {user?.createdAt && (
            <>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.memberSince")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {new Date(user.createdAt).toLocaleDateString()}
              </Typography>
            </>
          )}

          <Stack spacing={1.5} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              disabled={busy}
              fullWidth
            >
              {t("nav.signOut")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setDeleteOpen(true)}
              disabled={busy}
              fullWidth
            >
              {t("account.deleteAccount")}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleteOpen}
        title={t("account.deleteConfirmTitle")}
        message={t("account.deleteConfirmMessage")}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
